<script setup>
import { useLocalStorage } from '@vueuse/core'

const props = defineProps({
  comic: { type: [Object, null] /** @type {PropType<GCDComic>} */ },
  show: { type: Boolean, default: false },
})
const visible = ref(props.show)
const debug = useLocalStorage('debug-gcd-info', false)

const description = computed(() => {
  if (!props.comic) return ''

  const story = props.comic.story_set?.find(s => s.synopsis?.trim().length > 0)
  if (story) return story.synopsis

  const note = props.comic.story_set?.find(s => s.notes?.trim().length > 0)
  if (note) return note.notes

  return `${props.comic.editing || ''} ${props.comic.indicia_publisher || ''}`
})

const title = computed(() => {
  if (!props.comic) return ''

  const story = props.comic.story_set?.find(s => s.title?.trim().length > 0)
  if (story) return story.title

  if (props.comic.series_name) return props.comic.series_name

  return props.comic.api_url?.split('/').pop() || ''
})
const link = computed(() => {
  if (!props.comic) return '/'
  if (props.comic.link) return props.comic.link

  const u = new URL(props.comic.api_url)
  const match = u.pathname.match(/\/issue\/(\d+)/)
  const issue = match ? match[1] : null

  if (issue) return `https://www.comics.org/issue/${issue}/`
  return 'https://www.comics.org/'
})

const linkText = computed(() => {
  if (!props.comic) return ''
  if (props.comic.linkText) return props.comic.linkText

  return `comics.org`
})

const attribution = computed(() => {
  if (props.comic?.attribution === false) return ''
  return 'Grand Comics Database (GCD)'
})

const emit = defineEmits(['hide-info'])
const comicModalInfo = useTemplateRef('comic-modal-info')

watch(() => props.show, () => {
  visible.value = props.show
  if (props.show)
    comicModalInfo.value.showModal()
  else
    comicModalInfo.value.close()
})

onMounted(() => visible.value = props.show)

function onAfterModalHidden() {
  emit('hide-info')
  visible.value = false
}
</script>

<template>
  <Teleport to="#teleports">
    <dialog
      id="comic-modal-info"
      ref="comic-modal-info"
      class="modal"
      @cancel="onAfterModalHidden"
      @close="onAfterModalHidden"
    >
      <div class="modal-box relative">
        <form method="dialog">
          <button
            id="comic-modal-close-btn"
            class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2 z-10"
          >
            <Icon
              name="game-icons:crossed-swords"
              size="1.25rem"
            />
          </button>
        </form>
        <h3 class="text-lg font-bold w-fit">
          Comic information
        </h3>
        <pre v-if="debug">{{ JSON.stringify(comic, null, 2) }}</pre>
        <h2 class="font-semibold my-2">
          {{ title }}
        </h2>
        <p>
          {{ description }}
        </p>
        <br>
        <p v-if="attribution">
          {{ attribution }}
        </p>
        <p
          v-if="attribution"
          class="mt-2"
        >
          <span class="badge badge-warning">Please note</span> Comics are fetched at random from the GCD
          and may occasionally contain mature or unexpected content. I apologise for any offence; none is intended.
        </p>
        <div class="modal-action">
          <NuxtLink
            v-if="link && link.startsWith('/')"
            class="link link-hover"
            :to="link"
          >
            {{ linkText }}
          </NuxtLink>
          <NuxtLink
            v-else-if="link"
            class="link link-hover"
            :href="link"
            target="_blank"
          >
            {{ linkText }}
          </NuxtLink>
        </div>
      </div>
    </dialog>
  </Teleport>
</template>
